import React from 'react';
import { AlertCircle } from 'lucide-react';
import StepShell from './StepShell';
import StepTitleWithInfo from './StepTitleWithInfo';
import type { FlowCtx } from './types';

interface Props {
  ctx: FlowCtx;
}

const Step4Integrations: React.FC<Props> = ({ ctx }) => {
  const clearError = (key: keyof typeof ctx.validationErrors) => {
    if (ctx.validationErrors[key]) {
      ctx.setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const anyEnabled =
    (ctx.featureBmsEnabled && ctx.enableBms) ||
    (ctx.featureMqttEnabled && ctx.enableMqtt);

  return (
    <StepShell
      stepNumber={3}
      onBack={ctx.openExitModal}
      secondary={{ label: 'Back', onClick: () => ctx.goTo('wificonf') }}
      primary={{
        label: anyEnabled ? 'Next' : 'Skip',
        onClick: () => ctx.proceedFromIntegrations(),
      }}
    >
      <StepTitleWithInfo>
       <h2 className="dcf-step-title">
        Step 3 of 4: Configure integrations
      </h2>
      </StepTitleWithInfo>
      
      <div className="dcf-step-bar"></div>

      <form className="dcf-form" onSubmit={(e) => e.preventDefault()}>
        {ctx.featureBmsEnabled && (
          <>
            <div className="dcf-toggle">
              <div className="dcf-toggle__label">
                <h3 className="dcf-toggle__title">Enable BMS</h3>
                <p className="dcf-toggle__hint">
                  Connect and manage systems through BMS.
                </p>
              </div>
              <label className="dcf-switch">
                <input
                  type="checkbox"
                  checked={ctx.enableBms}
                  onChange={(e) => {
                    ctx.setEnableBms(e.target.checked);
                    clearError('bms');
                  }}
                />
                <span className="dcf-switch__slider" />
              </label>
            </div>

            {ctx.enableBms && (
              <div className="dcf-section">
                <h4 className="dcf-section__title">Protocol</h4>
                <div className="dcf-radio-row">
                  <label className="dcf-radio">
                    <input
                      type="radio"
                      name="bmsProtocol"
                      checked={ctx.bmsProtocol === 'bacnet'}
                      onChange={() => {
                        ctx.setBmsProtocol('bacnet');
                        clearError('bms');
                      }}
                    />
                    BACnet
                  </label>
                  <label className="dcf-radio">
                    <input
                      type="radio"
                      name="bmsProtocol"
                      checked={ctx.bmsProtocol === 'modbus'}
                      onChange={() => {
                        ctx.setBmsProtocol('modbus');
                        clearError('bms');
                      }}
                    />
                    Modbus
                  </label>
                </div>

                {ctx.bmsProtocol === 'bacnet' && (
                  <>
                    <div className="dcf-field">
                      <label className="dcf-field__label">BACnet Device ID</label>
                      <input
                        type="number"
                        value={ctx.bacnetDeviceId}
                        onChange={(e) => {
                          ctx.setBacnetDeviceId(e.target.value);
                          clearError('bms');
                        }}
                        placeholder="Enter the BACnet device ID"
                        className="dcf-field__input"
                      />
                    </div>
                    <div className="dcf-field">
                      <label className="dcf-field__label">BACnet Device Name</label>
                      <input
                        type="text"
                        value={ctx.bacnetDeviceName}
                        onChange={(e) => ctx.setBacnetDeviceName(e.target.value)}
                        placeholder="Enter the BACnet device name"
                        className="dcf-field__input"
                      />
                    </div>
                    <div className="dcf-field">
                      <label className="dcf-field__label">BACnet UDP Port</label>
                      <input
                        type="number"
                        value={ctx.bacnetPort}
                        onChange={(e) => ctx.setBacnetPort(e.target.value)}
                        placeholder="Default: 47808"
                        className="dcf-field__input"
                      />
                    </div>
                  </>
                )}

                {ctx.bmsProtocol === 'modbus' && (
                  <>
                    <div className="dcf-field">
                      <label className="dcf-field__label">Modbus TCP/UDP Port</label>
                      <input
                        type="number"
                        value={ctx.modbusPort}
                        onChange={(e) => {
                          ctx.setModbusPort(e.target.value);
                          clearError('bms');
                        }}
                        placeholder="Default: 502"
                        className="dcf-field__input"
                      />
                    </div>
                    <div className="dcf-field">
                      <label className="dcf-field__label">Modbus Device ID</label>
                      <input
                        type="number"
                        value={ctx.modbusDeviceId}
                        onChange={(e) => {
                          ctx.setModbusDeviceId(e.target.value);
                          clearError('bms');
                        }}
                        placeholder="Derived from serial when available"
                        className="dcf-field__input"
                      />
                    </div>
                    <h4 className="dcf-section__title">Connection Type</h4>
                    <div className="dcf-radio-row">
                      <label className="dcf-radio">
                        <input
                          type="radio"
                          name="modbusConnectionType"
                          checked={ctx.modbusConnectionType === 'TCP'}
                          onChange={() => ctx.setModbusConnectionType('TCP')}
                        />
                        TCP
                      </label>
                      <label className="dcf-radio">
                        <input
                          type="radio"
                          name="modbusConnectionType"
                          checked={ctx.modbusConnectionType === 'RTU'}
                          onChange={() => ctx.setModbusConnectionType('RTU')}
                        />
                        RTU
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}

            {ctx.validationErrors.bms && (
              <span className="dcf-field__error">
                <AlertCircle className="h-4 w-4" />
                {ctx.validationErrors.bms}
              </span>
            )}
          </>
        )}

        {ctx.featureMqttEnabled && (
          <>
            <div className="dcf-toggle">
              <div className="dcf-toggle__label">
                <h3 className="dcf-toggle__title">Enable MQTT</h3>
                <p className="dcf-toggle__hint">
                  Enable lightweight messaging via MQTT.
                </p>
              </div>
              <label className="dcf-switch">
                <input
                  type="checkbox"
                  checked={ctx.enableMqtt}
                  onChange={(e) => {
                    ctx.setEnableMqtt(e.target.checked);
                    clearError('mqtt');
                  }}
                />
                <span className="dcf-switch__slider" />
              </label>
            </div>

            {ctx.enableMqtt && (
              <div className="dcf-section">
                <div className="dcf-field">
                  <label className="dcf-field__label">MQTT Host</label>
                  <input
                    type="text"
                    value={ctx.mqttHost}
                    onChange={(e) => {
                      ctx.setMqttHost(e.target.value);
                      clearError('mqtt');
                    }}
                    placeholder="e.g. broker.emqx.io"
                    className="dcf-field__input"
                  />
                </div>
                <div className="dcf-field">
                  <label className="dcf-field__label">MQTT Port</label>
                  <input
                    type="number"
                    value={ctx.mqttPort}
                    onChange={(e) => {
                      ctx.setMqttPort(e.target.value);
                      clearError('mqtt');
                    }}
                    placeholder="1883"
                    className="dcf-field__input"
                  />
                </div>
                <label className="dcf-radio" style={{ marginTop: 4 }}>
                  <input
                    type="checkbox"
                    checked={ctx.mqttSsl}
                    onChange={(e) => {
                      const ssl = e.target.checked;
                      ctx.setMqttSsl(ssl);
                      // Default to the standard MQTT port for the chosen mode:
                      // 8883 for TLS, 1883 for plain.
                      ctx.setMqttPort(ssl ? '8883' : '1883');
                    }}
                  />
                  Use TLS (SSL)
                </label>
              </div>
            )}

            {ctx.validationErrors.mqtt && (
              <span className="dcf-field__error">
                <AlertCircle className="h-4 w-4" />
                {ctx.validationErrors.mqtt}
              </span>
            )}
          </>
        )}
      </form>
    </StepShell>
  );
};

export default Step4Integrations;
